import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import FloatingNav from '../components/FloatingNav';
import BackToHome from '../components/BackToHome';
import { ASSETS, STOCK_IMAGES } from '../config/assets';
import { MessageSquare, Eye, AlertCircle, ArrowRight, CheckCircle, Cpu } from 'lucide-react';

const PlatformPage = () => {
  const { t, language } = useLanguage();

  const features = [
    {
      icon: MessageSquare,
      title: t('platform.feature1Title'),
      desc: t('platform.feature1Desc'),
    },
    {
      icon: Eye,
      title: t('platform.feature2Title'),
      desc: t('platform.feature2Desc'),
    },
    {
      icon: AlertCircle,
      title: t('platform.feature3Title'),
      desc: t('platform.feature3Desc'),
    },
  ];

  const zeroIntegration = language === 'fr' ? [
    'Zéro intégration POS requise',
    'Pas de changements matériels',
    'Pas de goulets d\'étranglement IT',
    'Déploiement en jours, pas en mois'
  ] : [
    'Zero POS integration required',
    'No hardware changes',
    'No IT bottlenecks',
    'Deploy in days, not months'
  ];

  return (
    <div data-testid="platform-page" className="bg-[#0D0D0D]">
      {/* Floating Navigation */}
      <FloatingNav sections={[]} />
      <BackToHome />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center bg-gradient-to-b from-[#1A1A1A] to-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-wine mb-4 block">
              {t('platform.title')}
            </span>
            <h1 
              data-testid="platform-title"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6"
            >
              {t('platform.heroTitle')}
            </h1>
            <p className="text-lg sm:text-xl text-white/60 leading-relaxed mb-8">
              {t('platform.heroDesc')}
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                data-testid="platform-cta"
                className="btn-demo-cta text-white rounded-lg px-8 py-6"
              >
                {t('nav.requestDemo')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Zero Integration Section */}
      <section className="py-16 bg-[#0D0D0D] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Cpu className="w-6 h-6 text-wine" strokeWidth={1.5} />
            <span className="text-sm font-bold uppercase tracking-wider text-white/70">
              {language === 'fr' ? 'Indépendance Totale' : 'Total Independence'}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {zeroIntegration.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-white/80 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-28 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white">
              {t('platform.subtitle')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                data-testid={`platform-feature-${idx}`}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:-translate-y-1 hover:border-white/20 transition-all duration-300"
              >
                <div className="p-3 bg-white/5 rounded-xl w-fit mb-6">
                  <feature.icon className="w-6 h-6 text-wine" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-medium text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-white/50 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-20 lg:py-28 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                                src={ASSETS.DASHBOARD_SHIFT_INSIGHTS}
                alt="YUL On Tap Shift Insights - Real-Time Guest Intelligence"
                className="rounded-2xl shadow-2xl w-full object-cover border border-white/10"
              />
            </div>
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6">
                {t('platform.heroTitle')}
              </h2>
              <p className="text-white/60 text-lg leading-relaxed mb-8">
                {t('platform.heroDesc')}
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 text-wine font-medium hover:gap-3 transition-all">
                {t('nav.requestDemo')} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PlatformPage;
